import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import AdvisorChat from "@/components/AdvisorChat";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Hammer,
  Droplet,
  Zap,
  Home,
  Paintbrush,
  Square,
  Wind,
  Trees,
  ChefHat,
  ArrowRight,
  CheckCircle2,
  Users,
  Shield,
  Menu,
  X,
  Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Building2,
  Hammer,
  Droplet,
  Zap,
  Home,
  Paintbrush,
  Square,
  Wind,
  Trees,
  ChefHat,
};

const CATEGORY_SLUGS = {
  "Construction": "construction",
  "Renovation": "renovation",
  "Plumbing": "plumbing",
  "Electrical": "electrical",
  "Roofing": "roofing",
  "Painting": "painting",
  "Flooring": "flooring",
  "HVAC": "hvac",
  "Landscaping": "landscaping",
  "Kitchen & Bath": "kitchen-bath",
  "Construcción": "construction",
  "Renovación": "renovation",
  "Plomería": "plumbing",
  "Electricidad": "electrical",
  "Techos": "roofing",
  "Pintura": "painting",
  "Pisos": "flooring",
  "Climatización": "hvac",
  "Paisajismo": "landscaping",
  "Cocina y Baño": "kitchen-bath",
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Seed data and fetch categories
    const init = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        // Already seeded
      }
      
      try {
        const response = await axios.get(`${API}/categories?lang=${i18n.language?.startsWith('es') ? 'es' : 'en'}`);
        setCategories(response.data);
      } catch (e) {
        console.error("Failed to fetch categories:", e);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      if (user.user_type === "customer") navigate("/customer");
      else if (user.user_type === "business") navigate("/business");
      else if (user.user_type === "admin") navigate("/admin");
    } else {
      navigate("/register");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-[#0B1F3B] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <span className="text-xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
                My Project Bidder
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-slate-600 hover:text-[#0B1F3B] font-medium">
                {t("common.services")}
              </a>
              <a href="#how-it-works" className="text-slate-600 hover:text-[#0B1F3B] font-medium">
                {t("common.howItWorks")}
              </a>
              <Link to="/faq" className="text-slate-600 hover:text-[#0B1F3B] font-medium">
                {t("common.faq")}
              </Link>
              <LanguageSwitcher />
              {isAuthenticated ? (
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold px-6"
                  data-testid="dashboard-btn"
                >
                  {t("common.dashboard")}
                </Button>
              ) : (
                <div className="flex items-center gap-4">
                  <Link to="/login">
                    <Button variant="ghost" className="font-semibold text-[#0B1F3B]" data-testid="login-btn">
                      {t("common.logIn")}
                    </Button>
                  </Link>
                  <Button
                    onClick={() => navigate("/register")}
                    className="bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold px-6"
                    data-testid="register-btn"
                  >
                    {t("common.getStarted")}
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4">
            <div className="flex flex-col gap-4">
              <a href="#services" className="text-slate-600 hover:text-[#0B1F3B] font-medium py-2">
                {t("common.services")}
              </a>
              <a href="#how-it-works" className="text-slate-600 hover:text-[#0B1F3B] font-medium py-2">
                {t("common.howItWorks")}
              </a>
              <Link to="/faq" className="text-slate-600 hover:text-[#0B1F3B] font-medium py-2">
                {t("common.faq")}
              </Link>
              <LanguageSwitcher />
              {isAuthenticated ? (
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold w-full"
                >
                  {t("common.dashboard")}
                </Button>
              ) : (
                <>
                  <Link to="/login" className="w-full">
                    <Button variant="outline" className="w-full font-semibold">
                      {t("common.logIn")}
                    </Button>
                  </Link>
                  <Button
                    onClick={() => navigate("/register")}
                    className="bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold w-full"
                  >
                    {t("common.getStarted")}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-section blueprint-grid pt-24">
        <div className="hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" style={{ fontFamily: 'Manrope' }}>
                {t("landing.heroTitle1")}{" "}
                <span className="text-[#D4AF37]">{t("landing.heroTitle2")}</span> {t("landing.heroTitle3")}
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-lg">
                {t("landing.heroSubtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleGetStarted}
                  className="gold-gradient text-[#0B1F3B] hover:brightness-110 font-extrabold px-10 py-6 text-lg shadow-lg btn-transition"
                  data-testid="get-started-btn"
                >
                  {t("landing.getFreeEstimates")}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/register?type=business")}
                  className="border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-6 text-lg"
                  data-testid="join-as-business-btn"
                >
                  {t("landing.joinAsBusiness")}
                </Button>
              </div>
            </div>
            <div className="hidden md:block animate-fade-in-up stagger-2">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1773430273188-bd58cb3aaabf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBwcm9mZXNzaW9uYWwlMjBob21lJTIwaW1wcm92ZW1lbnR8ZW58MHx8fHwxNzc5MjQ4MjM1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Construction workers building a wooden deck"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Pills */}
      <section className="py-10 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0B1F3B]/5 border border-[#0B1F3B]/10">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              <span className="font-semibold text-[#0B1F3B]">{t("landing.verified")}</span>
            </span>
            <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0B1F3B]/5 border border-[#0B1F3B]/10">
              <Users className="w-5 h-5 text-[#D4AF37]" />
              <span className="font-semibold text-[#0B1F3B]">{t("landing.contractors")}</span>
            </span>
            <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0B1F3B]/5 border border-[#0B1F3B]/10">
              <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
              <span className="font-semibold text-[#0B1F3B]">{t("landing.satisfaction")}</span>
            </span>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#0B1F3B]/10 text-[#0B1F3B] font-semibold text-sm rounded-full mb-4">
              {t("landing.ourServices")}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0B1F3B] mb-4" style={{ fontFamily: 'Manrope' }}>
              {t("landing.servicesTitle")}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t("landing.servicesSubtitle")}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {categories.map((category, idx) => {
              const IconComponent = iconMap[category.icon] || Hammer;
              const slug = CATEGORY_SLUGS[category.name] || category.name.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-');
              return (
                <Link
                  to={`/services/${slug}`}
                  key={category.id}
                  className="category-card category-card-link group animate-fade-in-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                  data-testid={`category-${category.id}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-[#F8FAFC] group-hover:bg-[#D4AF37]/10 flex items-center justify-center mb-4 transition-colors duration-200">
                    <IconComponent className="w-6 h-6 text-[#0B1F3B] group-hover:text-[#D4AF37]" />
                  </div>
                  <h3 className="font-bold text-[#0B1F3B] mb-1">{category.name}</h3>
                  <p className="text-sm text-slate-500 line-clamp-2">{category.description}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 md:py-28 bg-[#0B1F3B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#D4AF37]/20 text-[#D4AF37] font-semibold text-sm rounded-full mb-4">
              {t("landing.simpleProcess")}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
              {t("landing.howItWorksTitle")}
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t("landing.howItWorksSubtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: t("landing.step01"),
                description: t("landing.step01Desc"),
              },
              {
                step: "02",
                title: t("landing.step02"),
                description: t("landing.step02Desc"),
              },
              {
                step: "03",
                title: t("landing.step03"),
                description: t("landing.step03Desc"),
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="relative bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 animate-fade-in-up"
                style={{ animationDelay: `${idx * 0.15}s` }}
              >
                <span className="text-5xl font-extrabold text-[#D4AF37]/30 absolute top-4 right-4">
                  {item.step}
                </span>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }}>
                  {item.title}
                </h3>
                <p className="text-slate-300">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={handleGetStarted}
              className="gold-gradient text-[#0B1F3B] hover:brightness-110 font-extrabold px-10 py-6 text-lg shadow-lg btn-transition"
              data-testid="cta-get-estimates-btn"
            >
              {t("landing.startGettingEstimates")}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* For Businesses Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.pexels.com/photos/5493672/pexels-photo-5493672.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional contractor using drill during renovation"
                className="rounded-2xl shadow-float"
              />
            </div>
            <div className="order-1 md:order-2">
              <span className="inline-block px-4 py-2 bg-[#0B1F3B]/10 text-[#0B1F3B] font-semibold text-sm rounded-full mb-4">
                {t("landing.forContractors")}
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0B1F3B] mb-6" style={{ fontFamily: 'Manrope' }}>
                {t("landing.growWithApp")}
              </h2>
              <ul className="space-y-4 mb-8">
                {[
                  t("landing.benefit1"),
                  t("landing.benefit2"),
                  t("landing.benefit3"),
                  t("landing.benefit4"),
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate("/register?type=business")}
                className="bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold px-8 py-6 text-lg"
                data-testid="join-network-btn"
              >
                {t("landing.joinNetwork")}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* AI Project Advisor */}
      <section className="py-20 md:py-28 bg-white" id="advisor">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1.5 mb-4">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-medium text-[#0B1F3B]">{t("landing.aiPowered")}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0B1F3B] mb-3" style={{ fontFamily: 'Manrope' }}>
              {t("landing.notSureTitle")}
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              {t("landing.notSureSubtitle")}
            </p>
          </div>
          <AdvisorChat />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B1F3B] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#0B1F3B]" />
              </div>
              <span className="text-xl font-extrabold text-white" style={{ fontFamily: 'Manrope' }}>
                {t("common.appNameSpaced")}
              </span>
            </div>
            <div className="flex items-center gap-6 text-slate-400">
              <a href="#services" className="hover:text-white">{t("common.services")}</a>
              <a href="#how-it-works" className="hover:text-white">{t("common.howItWorks")}</a>
              <Link to="/faq" className="hover:text-white">{t("common.faq")}</Link>
              <Link to="/login" className="hover:text-white">{t("common.login")}</Link>
            </div>
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} {t("common.appNameSpaced")}. {t("landing.allRightsReserved")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
